import { dataService } from '@app/modules/data';
import { DataObject, ObjectOptions } from './base';

import { Place } from './place';
import { Session } from './session';
import { Ticket } from './ticket';
import { CashChange } from './cashchange';
import { AuditInfo } from './auditinfo';
import { AuditTill } from './audittill';
import { PlaceService } from './svcplace';

export interface _Audit {
    place: number;
    session: number;
    cash: number;
};

interface _AuditData extends _Audit {
    objid?: number;
    _uuid?: string;
    created?: Date;
};

abstract class AuditData extends DataObject {
    protected _audit: _AuditData = {
        place: null,
        session: null,
        cash: null
    };

    constructor(table: string, objid: string, data: dataService, objoptions: ObjectOptions){
        super(table, objid, data, objoptions);
        this._audit.created = new Date();
    }

    /****************************/
    /* CLASS MEMBERS            */
    /****************************/

    get created(){
        return this._audit.created;
    }

    get place(): Place{
        return this._children['place'] || null;
    }

    set place(value: Place){
        if (this.SetChild('place', value, 'place')){
            this.ToUpdate = true;
        }
    }

    get session(): Session{
        return this._children['session'] || null;
    }

    set session(value: Session){
        if (this.SetChild('session', value, 'session')){
            this.ToUpdate = true;
        }
    }

    get cashchanges(): Array<CashChange> {
        return this._chldlist['cashchange'] || [];
    }

    get cash(): number{
        return this._audit.cash;
    }

    set cash(value: number){
        if (this.patchValue(this._audit, 'cash', value)){
            this.ToUpdate = true;
        }
    }

    // Per-place cierre number for display: 1-based ordinal of this audit
    // among the place's audits sorted by (created, objid), zero-padded to
    // 4 digits. Computed client-side on every read — cheap (a place has
    // at most a few hundred audits in scope) and avoids carrying a
    // persisted sequence column + DB-side counter just to render a label.
    // Empty when the audit isn't in place.audits yet (volatile / not
    // committed) so the UI hides the field rather than showing 0.
    get AuditId(): string {
        let _place = this.place;
        if (!_place || !_place.audits){
            return '';
        }
        let _sorted = [..._place.audits].sort((a, b) => {
            let _ta = a.created.getTime();
            let _tb = b.created.getTime();
            if (_ta != _tb) return _ta - _tb;
            return (Number(a.objid) || 0) - (Number(b.objid) || 0);
        });
        let _idx = _sorted.indexOf(this as unknown as Audit);
        if (_idx == -1){
            return '';
        }
        return String(_idx + 1).padStart(4, '0');
    }

    get auditinfo(): AuditInfo {
        let _auditinfos = this._chldlist['auditinfo'] || [];
        if (_auditinfos.length > 0){
            return _auditinfos[0]
        }
    
        return null;    // no audit information for this audit?
    }

    protected get tills(): Array <AuditTill> {
        return this._chldlist['till'] || [];
    }

    /****************************/
    /* COMMIT OPERATION         */
    /****************************/

    protected get Change(){
        return {
            place: this._audit.place,
            session: this._audit.session,
            cash: this._audit.cash
        };
    }

    protected get Depend() {
        return {
            place: { item: this.place, relation_info: { to: 'audits', by: 'place' } },  // this[by -> 'place'][to -> 'audits'] => this
        };
    }

    protected get tickets() : Array <Ticket> {
        return [];      // implemented on derived class
    }

    protected get Children() {
        let _children = [];

        for(let _item of this.cashchanges){
            _children.push(_item);
        }

        for(let _item of this.tickets){
            _children.push(_item);
        }

        /*  audittill entries must be sent prior to auditinfo entries */

        for(let _item of this.tills){
            _children.push(_item);
        }

        if (this.auditinfo){
            _children.push(this.auditinfo);
        }

        return _children;
    }
    
    /****************************/
    /* DATA OBJECT              */
    /****************************/

    private _patchData(_audit: _Audit){
        let _toUpdate = false;

        _toUpdate = this.patchValue(this._audit, 'place', _audit['place']) || _toUpdate;
        _toUpdate = this.patchValue(this._audit, 'session', _audit['session']) || _toUpdate;
        _toUpdate = this.patchValue(this._audit, 'cash', _audit['cash']) || _toUpdate;

        return _toUpdate;
    }
    
    set Data(_audit: _Audit){
        this.patchValue(this._audit, 'created', _audit['created']);
        
        if (this._patchData(_audit)){
            this.ToUpdate = true;
        }
    }

    get Info(){
        return this._audit;
    }

    set Info(value){
        this.DoPatchValues(value);
    }

    private _ddbb(info): _AuditData {
        let _audit: _AuditData = {
            objid: info['objid'] ? parseInt(info['objid']) : null,
            created: new Date(Date.parse(this.mysqlToDateStr(info['created']))),
            place: info['place'] ? parseInt(info['place']) : null,
            session: info['session'] ? parseInt(info['session']) : null,
            cash: info['cash'] ? parseFloat(info['cash']) : 0
        };
        return _audit;
    }

    private DoPatchValues(_audit: _Audit){
        this._patchData(_audit);

        if (_audit['session']){     // update children: 'session'
            let _objid = _audit['session'].toString();
            this.SetChild('session', new Session(_objid, this.data), 'session');
        }
        else {
            this.SetChild('session', null, 'session');
        }

        if (_audit['place']){     // update children: 'place'
            let _objid = _audit['place'].toString();
            this.SetChild('place', new Place(_objid, this.data, this._objoptions), 'place');
        }  
        else {
            this.SetChild('place', null, 'place');
        }
    }

    protected _OnUpdate(info){
        let _audit = this._ddbb(info);
        this.patchValue(this._audit, 'objid', _audit['objid']);
        this.patchValue(this._audit, 'created', _audit['created']);
        this.DoPatchValues(_audit);

        if ('cashchange' in info) {  // update children: 'cashchange'
            this.SetChildren <CashChange> (info['cashchange'], 'cashchange', CashChange, 'audit');
        }

        if ('till' in info) {  // update children: 'till'
            this.SetChildren <AuditTill> (info['till'], 'till', AuditTill, 'audit');
        }        

        if ('auditinfo' in info) {  // update children: 'auditinfo'
            this.SetChildren <AuditInfo> (info['auditinfo'], 'auditinfo', AuditInfo, 'audit');
        }
    }
}

export class Audit extends AuditData {
    constructor(objid: string, data: dataService, objoptions: ObjectOptions = null){
        super('AUDIT', objid, data, objoptions);
    }

    Copy(store: Array<DataObject> = []): Audit {
        return this._Copy(store) as Audit;
    }

    /****************************/
    /* CUSTOM MEMBERS           */
    /****************************/

    get till(): PlaceService {
        let _tills = this.tills;
        if (_tills.length > 0){
            return _tills[0].till;
        }
        return null;
    }

    set till(value: PlaceService){
        if (value == null){
            console.warn("[AUDIT] Cannot set an invalid till to this audit! Operation ignored");
            return;
        }

        let _till = this.till;
        if (_till){
            console.warn("[AUDIT] Cannot change this audit's till! Operation ignored");
        }
        else {
            let _audittill = new AuditTill(null, this.data);
            if (_audittill){
                _audittill.audit = this;
                _audittill.till = value;
                _audittill.status = 'AC';
            }

            this.AddChild('till', _audittill, 'audit');
        }
    }

    /****************************/
    /* CHILDREN MANAGEMENT      */
    /****************************/
    
    protected AddTicket(child: Ticket){
        if (child){
            child.audit = this;     // assing the audit to the ticket

            if (!('ticket' in this._children)){
                this._children['ticket'] = new Set <Ticket> ();
            }

            this._children['ticket'].add(child);
        }
    }

    /****************************/
    /* CHILD ACCESS             */
    /****************************/
    
    protected get tickets() : Array <Ticket> {
        if ('ticket' in this._children){
            return [...this._children['ticket']];
        }

        return [];
    }

    /****************************/
    /* CUSTOM METHODS           */
    /****************************/

    get IsValid(){
        return this.IsLoaded;
    }

    get IsCommited(){
        return (this.ToInsert == false) && (this.objid != null);
    }

    InTill(till){
        if (this.place.MultiplePOS){
            return this.till == till;
        }
        return true;    // single POS
    }

    // (created, objid) ordering — the same tiebreaker the server-side
    // audit query uses (created ASC, objid ASC). Two cierres closed in
    // the same second are ordered by objid instead of both being skipped.
    private _isAuditBefore(a: Audit, b: Audit): boolean {
        let _ta = a.created.getTime();
        let _tb = b.created.getTime();
        if (_ta != _tb){
            return _ta < _tb;
        }
        return (a.objid != null) && (b.objid != null) && (Number(a.objid) < Number(b.objid));
    }

    get Previous() : Audit {
        // The previous audit is the most recent one — on the same till
        // when multiple POS — that comes before this one. Ordering is by
        // (created, objid), not by array identity, so it stays correct
        // even when this Audit isn't the instance held in place.audits
        // (e.g. the print report builds its own) and a same-second twin
        // is still resolved as the previous audit instead of skipped.
        let _previous: Audit = null;
        for (let _audit of this.place.audits){
            if (!this.InTill(_audit.till)){
                continue;
            }
            if (!this._isAuditBefore(_audit, this)){
                continue;
            }
            if (!_previous || this._isAuditBefore(_previous, _audit)){
                _previous = _audit;
            }
        }

        return _previous;
    }

    private _audittickets_promise: Promise <Array <Ticket>> = null;
    private get _AuditTickets(): Promise <Array <Ticket>> {
        // cache the in-flight (and resolved) promise to avoid duplicate LoadPlaceTickets
        // calls when both ReloadInfo() and TaxBreakdown() run for the same audit.
        // Use an IIFE instead of `new Promise(async (resolve) => ...)` so a
        // rejection in LoadPlaceTickets propagates; clear the cache on failure
        // so a retry is possible instead of leaving the consumer stuck on a
        // pending promise forever.
        if (!this._audittickets_promise){
            this._audittickets_promise = (async () => {
                try {
                    let _tickets: Array <Ticket> = [];

                    let _prev = this.Previous?.created || null;     // audit ini date
                    let _next = this.created;                       // audit end date

                    // Source the candidate tickets without LoadPlaceTickets'
                    // pre-filter by `updated`: that filter diverges from the
                    // server-side auditinfo.php counter, which is `paidon`-based
                    // for not-cancelled tickets. Going through it would silently
                    // drop tickets cobrados dentro del rango pero con `updated`
                    // fuera (p.ej. ticket abierto en sesion anterior y cobrado
                    // ahora, o modificado tras cobrar), leaving the series-range
                    // count short of "Tickets completados" on the print.
                    //
                    // When the whole audit range is recent we iterate the
                    // place's in-memory tickets and let InAudit apply the
                    // precise filter (the same one the server uses). The
                    // decision keys off _prev (the range START): place.tickets
                    // only holds roughly the last few days, so a close made
                    // today whose previous audit was months ago is NOT covered
                    // — keying off _next there left the tax breakdown counting
                    // just the cached tail. For those — and the first audit
                    // ever (_prev == null) — fall back to LoadPlaceTickets: its
                    // updated-vs-paidon mismatch still exists, but it is a far
                    // smaller error than missing most of the range.
                    let _candidates: Array <Ticket>;
                    let _fromServer = false;
                    let _isRecent = (_prev != null) && ((Date.now() - _prev.getTime()) < (5 * 24 * 3600 * 1000));
                    if (_isRecent && this.objid){
                        // Cierre YA commiteado (thumb, tiene objid): el modelo en
                        // memoria basta y evita consultar el server por cada thumb.
                        _candidates = this.place.tickets;
                    }
                    else if (_isRecent && !this.objid){
                        // Cierre EN CURSO (aun sin objid): unir lo escrito en la BD con la
                        // memoria para cubrir los DOS desfases posibles:
                        //  - BD adelantada a memoria: ticket cobrado y persistido pero aun
                        //    ausente del modelo local (el bug original: quedaba fuera del
                        //    cierre hasta recargar).
                        //  - Memoria adelantada a BD: cobro local con el write todavia en
                        //    cola (status PD fija paidon al instante), aun no en la BD.
                        let _server: Array<Ticket> = [];
                        try {
                            _server = (await this.data.Reports.LoadUpdatedTickets(this.place, _prev, _next)) || [];
                        }
                        catch (err) {
                            console.warn("[AUDIT] LoadUpdatedTickets threw on close", err);
                        }

                        // Filtrar por InAudit ANTES de hidratar: LoadUpdatedTickets es por
                        // `updated` y trae tickets que pueden no pertenecer a este cierre.
                        // InAudit solo usa campos de cabecera (los ligeros los traen), asi
                        // que solo rehidratamos (ticketnfo.php) los que de verdad entran —
                        // evita cargas innecesarias en locales con mucho movimiento. Mismo
                        // patron que la rama de rangos antiguos (filtra ligeros, luego
                        // hidrata). No cambia el resultado: el bucle InAudit de mas abajo
                        // vuelve a filtrar el merge completo.
                        _server = _server.filter(_t => _t.InAudit(_prev, _next, this));

                        // Los tickets del server llegan ligeros (sin lineas). Hidratarlos
                        // ya, para que el desglose de IVA tenga datos; los de memoria ya
                        // traen sus lineas. (No se marca _fromServer: la hidratacion de la
                        // rama de rangos antiguos, mas abajo, no debe repetirse aqui.)
                        if (_server.length > 0){
                            try {
                                // LoadTicketObjects tampoco lanza en error: resuelve [] o
                                // parcial. Mezclar por objid (no reemplazar _server ciega-
                                // mente): cada ligero toma su version hidratada si volvio, y
                                // si no, se queda ligero (mejor un ticket sin lineas -> IVA
                                // corto que perderlo del cierre). Asi un resultado parcial no
                                // descarta los que no se pudieron rehidratar.
                                let _full = await this.data.LoadTicketObjects(_server);
                                if (_full && (_full.length > 0)){
                                    let _fullById = new Map<string, Ticket>();
                                    for (let _f of _full){
                                        if (_f.objid){
                                            _fullById.set(_f.objid, _f);
                                        }
                                    }
                                    _server = _server.map(_t => (_t.objid && _fullById.get(_t.objid)) || _t);
                                }
                            }
                            catch (err) {
                                console.warn("[AUDIT] LoadTicketObjects failed on close — using light server tickets", err);
                            }
                        }

                        // Merge dedup por objid. Por DEFECTO gana el de memoria, que trae
                        // sus lineas garantizadas; la BD solo lo sustituye cuando APORTA un
                        // cobro que la memoria aun no tiene (BD adelantada: server pagado,
                        // memoria no). Asi no se degrada un ticket de memoria hidratado a
                        // uno ligero del server (p.ej. cancelados, que InAudit incluye por
                        // updated y que dejarian el IVA corto si LoadTicketObjects fallo).
                        // El caso inverso (memoria adelantada: cobro local con el write en
                        // cola) se mantiene solo porque no se sustituye. Locales sin objid
                        // (nunca en la BD) se incluyen tal cual. Sin red, _server es []
                        // (LoadUpdatedTickets traga el error) y quedan solo los de memoria.
                        let _merged = new Map<string, Ticket>();
                        let _extra: Array<Ticket> = [];
                        for (let _t of this.place.tickets){
                            if (_t.objid){
                                _merged.set(_t.objid, _t);
                            }
                            else {
                                _extra.push(_t);
                            }
                        }
                        for (let _t of _server){
                            if (!_t.objid){
                                continue;
                            }
                            let _cur = _merged.get(_t.objid);
                            if (!_cur || ((_t.paidon != null) && (_cur.paidon == null))){
                                _merged.set(_t.objid, _t);
                            }
                        }
                        _candidates = _extra.concat(Array.from(_merged.values()));
                    }
                    else {
                        _candidates = await this.data.LoadPlaceTickets(this.place, _prev, _next, true);
                        _fromServer = true;
                    }

                    for(let _ticket of (_candidates || [])){
                        if (!_ticket.InAudit(_prev, _next, this)){
                            continue;   // this ticket does not belong to this audit
                        }

                        _tickets.push(_ticket);
                    }

                    // Only the older-audits path (_fromServer, via LoadPlaceTickets)
                    // reaches here with light tickets: the in-progress close already
                    // hydrated its server tickets above and does NOT set _fromServer,
                    // so it skips this block. Those light tickets arrive without their
                    // lines. Ticket.SplitTaxes walks products/offers/extras/
                    // discounts, so without the lines the tax breakdown
                    // comes out empty. Reload the full ticket objects
                    // (ticketnfo.php) so the breakdown has real data.
                    // InAudit only needs header fields, so it was safe to
                    // filter on the light tickets first and hydrate just
                    // the ones that belong to this audit.
                    if (_fromServer && (_tickets.length > 0)){
                        try {
                            let _full = await this.data.LoadTicketObjects(_tickets);
                            if (_full && (_full.length > 0)){
                                if (_full.length != _tickets.length){
                                    console.warn("[AUDIT] rehydrated " + _full.length + "/" + _tickets.length + " audit tickets — tax breakdown may be short");
                                }
                                _tickets = _full;
                            }
                        }
                        catch (err) {
                            console.warn("[AUDIT] LoadTicketObjects failed — using light tickets", err);
                        }
                    }

                    return _tickets;
                } catch (err) {
                    this._audittickets_promise = null;  // allow retry on next access
                    throw err;
                }
            })();
        }

        return this._audittickets_promise;
    }

    async ReloadInfo(){
        if (this.auditinfo == null){
            let _auditinfo = new AuditInfo(null, this.data);
            if (_auditinfo){
                _auditinfo.audit = this;
            }

            this.AddChild('auditinfo', _auditinfo, 'audit');
        }

        let _tickets = await this._AuditTickets;
        if (this.auditinfo != null){
            this.auditinfo.Reload(_tickets);

            if (this.place.MultiplePOS){
                for(let _ticket of _tickets){
                    // Already assigned to an audit (or legacy intill): skip.
                    if (_ticket.audit){
                        continue;
                    }

                    this.AddTicket(_ticket);    // creates TICKETAUDIT on commit

                    // Stale ToUpdate on DE/CC must not ride in the same sync
                    // batch as the cierre: a doomed TICKET do_update used to
                    // NotFound in post_complete and roll back the whole close.
                    // TicketAudit still commits via Children even if ToUpdate
                    // is cleared.
                    if (['DE', 'CC'].includes(_ticket.status)){
                        _ticket.ToUpdate = false;
                    }
                }
            }
        }

        this.auditinfo.DoRefresh('AUDITINFO');
    }

    async TicketSeriesRange(): Promise <Array <{ series: string, first: string, last: string, count: number }>> {
        let _tickets = await this._AuditTickets;

        let _byseries = new Map<string, { first: string, last: string, count: number }> ();
        for (let _ticket of _tickets){
            let _series  = _ticket.series  || '';
            let _invceno = _ticket.invceno || '';
            if (!_invceno){
                // No invoice number assigned -> did not consume a slot in the
                // series. This already excludes drafts and never-invoiced
                // cancellations; a "burnt" number (aborted signature) leaves NO
                // ticket carrying that invceno, so it never inflates the count.
                continue;
            }

            // Do NOT gate on IsValid here: a ticket that issued its invoice and
            // was later refunded / rectified / voided (status DE or CC) still
            // consumed that invoice number and was notified to TICKETBAI, so it
            // must be counted in the series range — otherwise the printed count
            // fell one short of "Tickets completados" (e.g. T..11-T..15 showing
            // 4 instead of 5). Open/PR tickets never reach here: _AuditTickets'
            // InAudit filter drops them (paidon null).

            let _entry = _byseries.get(_series);
            if (!_entry){
                _byseries.set(_series, { first: _invceno, last: _invceno, count: 1 });
            }
            else {
                if (_invceno < _entry.first) _entry.first = _invceno;
                if (_invceno > _entry.last)  _entry.last  = _invceno;
                _entry.count++;
            }
        }

        let _result = [];
        let _series_keys = Array.from(_byseries.keys()).sort();
        for (let _series of _series_keys){
            let _entry = _byseries.get(_series);
            _result.push({
                series: _series,
                first:  _entry.first,
                last:   _entry.last,
                count:  _entry.count
            });
        }

        return _result;
    }
}

